import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { NAV } from "../lib/site";
import Logo from "./Logo";

export const Eyebrow = ({ children, dark = false, className = "" }) => (
    <p className={`eyebrow inline-flex items-center gap-2.5 ${dark ? "text-cloud" : "text-slategray"} ${className}`}>
        <span className="inline-block size-[7px] rotate-45 bg-vantera" aria-hidden="true" />
        {children}
    </p>
);

export const Masked = ({ lines, as: Tag = "h1", className = "", delay = 0, inview = false, itemClassName = "" }) => {
    const reduce = useReducedMotion();
    const anim = (i) =>
        reduce
            ? {}
            : inview
            ? { initial: { y: "112%" }, whileInView: { y: 0 }, viewport: { once: true, margin: "-8% 0px" }, transition: { duration: 0.95, ease: [0.16, 1, 0.3, 1], delay: delay + i * 0.1 } }
            : { initial: { y: "112%" }, animate: { y: 0 }, transition: { duration: 0.95, ease: [0.16, 1, 0.3, 1], delay: delay + i * 0.1 } };
    return (
        <Tag className={className}>
            {lines.map((l, i) => (
                <span key={i} className={`block overflow-hidden ${itemClassName}`}>
                    <motion.span className="block will-change-transform" {...anim(i)}>
                        {l}
                    </motion.span>
                </span>
            ))}
        </Tag>
    );
};

export const Reveal = ({ children, delay = 0, y = 26, className = "", once = true }) => (
    <motion.div
        className={className}
        initial={{ opacity: 0, y }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once, margin: "-10% 0px" }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay }}
    >
        {children}
    </motion.div>
);

export const Crosshair = ({ pos = "top-0 left-0", dark = false }) => (
    <span aria-hidden="true" className={`card-plus ${pos} ${dark ? "!text-ivory/30" : ""}`}>+</span>
);

export const ArrowLink = ({ to, children, dark = false, testid, onClick, external = false }) => {
    const cls = `group inline-flex items-center gap-3 font-display font-bold text-[15px] tracking-tight ${dark ? "text-ivory" : "text-ink"}`;
    const inner = (
        <>
            <span className="relative">
                {children}
                <span className={`absolute -bottom-1 left-0 h-px w-full origin-left scale-x-100 transition-transform duration-300 group-hover:scale-x-0 ${dark ? "bg-ivory/40" : "bg-ink/40"}`} />
                <span className="absolute -bottom-1 left-0 h-px w-full origin-right scale-x-0 bg-vantera transition-transform duration-300 delay-100 group-hover:scale-x-100" />
            </span>
            <span className="transition-transform duration-300 group-hover:translate-x-1 text-vantera">→</span>
        </>
    );
    if (external) return <a href={to} className={cls} data-testid={testid} onClick={onClick}>{inner}</a>;
    return (
        <Link to={to} className={cls} data-testid={testid} onClick={onClick}>
            {inner}
        </Link>
    );
};

export const PrimaryCTA = ({ to = "/contact", children, testid = "primary-cta", dark = false, onClick, className = "" }) => (
    <Link
        to={to}
        data-testid={testid}
        onClick={onClick}
        className={`group relative inline-flex items-center gap-3 overflow-hidden rounded-full px-7 py-3.5 font-display text-[15px] font-bold tracking-tight transition-colors duration-300 ${
            dark ? "bg-vantera text-ink hover:bg-ivory" : "bg-ink text-ivory hover:bg-vantera hover:text-ink"
        } ${className}`}
    >
        <span className="relative z-10">{children}</span>
        <span aria-hidden="true" className="relative z-10 transition-transform duration-300 group-hover:translate-x-1">→</span>
    </Link>
);

export const ExternalCTA = ({ to, children, dark = false, testid = "external-cta" }) => (
    <a
        href={to}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={testid}
        className={`inline-flex items-center gap-2.5 rounded-full border px-7 py-3.5 font-display text-[15px] font-bold tracking-tight transition-colors duration-300 ${
            dark ? "border-ivory/25 text-ivory hover:border-vantera hover:text-vantera" : "border-ink/20 text-ink hover:border-vantera hover:text-vantera"
        }`}
    >
        {children}
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <path d="M2.5 9.5L9.5 2.5M9.5 2.5H4M9.5 2.5V8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    </a>
);

export const GhostCTA = ({ to = "/how-it-works", children, dark = false, testid = "secondary-cta", onClick }) => (
    <Link
        to={to}
        data-testid={testid}
        onClick={onClick}
        className={`inline-flex items-center gap-3 rounded-full border px-7 py-3.5 font-display text-[15px] font-bold tracking-tight transition-colors duration-300 ${
            dark ? "border-ivory/25 text-ivory hover:border-vantera hover:text-vantera" : "border-ink/20 text-ink hover:border-vantera hover:text-vantera"
        }`}
    >
        {children}
    </Link>
);

export const CountUp = ({ to, decimals = 0, prefix = "", suffix = "", duration = 1.8, className = "" }) => {
    const ref = useRef(null);
    const [val, setVal] = useState(0);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        let raf;
        const io = new IntersectionObserver(
            ([e]) => {
                if (!e.isIntersecting) return;
                io.disconnect();
                const t0 = performance.now();
                const tick = (t) => {
                    const p = Math.min(1, (t - t0) / (duration * 1000));
                    setVal(to * (1 - Math.pow(1 - p, 3)));
                    if (p < 1) raf = requestAnimationFrame(tick);
                };
                raf = requestAnimationFrame(tick);
            },
            { threshold: 0.4 }
        );
        io.observe(el);
        return () => {
            io.disconnect();
            if (raf) cancelAnimationFrame(raf);
        };
    }, [to, duration]);
    return (
        <span ref={ref} className={className}>
            {prefix}
            {val.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}
            {suffix}
        </span>
    );
};

export const Marquee = ({ items, dark = false, className = "" }) => (
    <div className={`overflow-hidden border-y ${dark ? "border-white/10 bg-ink text-ivory" : "border-ink/10 bg-ivory text-ink"} ${className}`} aria-hidden="true">
        <div className="marquee-track py-4">
            {[0, 1].map((copy) => (
                <div key={copy} className="flex shrink-0 items-center">
                    {items.map((it, i) => (
                        <span key={i} className="flex items-center">
                            <span className="whitespace-nowrap px-6 font-display text-sm font-bold uppercase tracking-[0.3em] sm:text-base">{it}</span>
                            <span className="inline-block size-1.5 rotate-45 bg-vantera" />
                        </span>
                    ))}
                </div>
            ))}
        </div>
    </div>
);

export const IllustrativeBadge = ({ dark = false }) => (
    <span className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] ${dark ? "border-white/15 text-cloud" : "border-ink/15 text-slategray"}`}>
        <span className="inline-block size-1.5 rounded-full bg-vantera animate-blink" data-testid="illustrative-badge" />
        Illustrative Opportunity Analysis
    </span>
);